import { useEffect, useRef } from "react";
import { type UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "~/components/ui";
import { useCategoriesModelContext } from "~/features/admin/categories/hooks";
import type { CategoryFormData, ParentCategoryOption } from "~/features/admin/categories/types";
import { buildCategoryPath, isValidCategoryPath, toPathSegment } from "~/features/admin/categories/utils/categoryPath";
import { cn } from "~/lib/utils";
import { ParentCategoryCombobox } from "./ParentCategoryCombobox";

interface CategoryFormFieldsProps {
  form: UseFormReturn<CategoryFormData>;
  formId: string;
  onSubmit: (data: CategoryFormData) => void;
  /** Selectable parents (own subtree already excluded when editing). */
  parentOptions: ParentCategoryOption[];
  /** Whether "no parent (root)" is a meaningful choice (editing a root category). */
  allowRootParent: boolean;
}

/**
 * Shared field set used by the Create and Edit dialogs. Reads the
 * category-type / relation-type lookups via Suspense so the form mounts
 * with stable option lists.
 *
 * The hierarchy is edited as "parent category" + "own path segment"; the
 * dot-joined path is previewed read-only and assembled on submit.
 */
export function CategoryFormFields({
  form,
  formId,
  onSubmit,
  parentOptions,
  allowRootParent,
}: CategoryFormFieldsProps) {
  const { t } = useTranslation();
  const { categoryTypes, relationTypes } = useCategoriesModelContext();
  const [typeId, name, parentPath, pathSegment] = useWatch({
    control: form.control,
    name: ["typeId", "name", "parentPath", "pathSegment"],
  });
  const isEdit = Boolean(form.getValues("id"));

  // Fill the segment from the name until the user edits the segment themselves (create only).
  const segmentTouched = useRef(false);
  useEffect(() => {
    if (isEdit || segmentTouched.current) return;
    form.setValue("pathSegment", toPathSegment(name ?? ""), { shouldValidate: form.formState.isSubmitted });
  }, [name, isEdit, form]);

  const previewPath = buildCategoryPath(parentPath ?? null, pathSegment || "…");
  const previewInvalid = Boolean(pathSegment) && !isValidCategoryPath(previewPath);

  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="typeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.category.type")}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("model.category.type")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {categoryTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="parentPath"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t("model.category.parent")}</FormLabel>
              <FormControl>
                <ParentCategoryCombobox
                  options={parentOptions}
                  value={field.value ?? null}
                  onChange={field.onChange}
                  label={t("model.category.parent")}
                  allowRoot={allowRootParent}
                  ariaInvalid={Boolean(fieldState.error)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="pathSegment"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.category.pathSegment")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    className="font-mono"
                    onChange={(e) => {
                      segmentTouched.current = true;
                      field.onChange(e);
                    }}
                  />
                </FormControl>
                <FormDescription>{t("feature.categories.form.pathSegmentHelp")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="space-y-2">
            <Label>{t("feature.categories.form.pathPreview")}</Label>
            <p
              className={cn(
                "rounded-[var(--radius-sm)] border border-dashed border-border-default bg-bg-subtle px-3 py-2 font-mono text-14-regular",
                previewInvalid ? "text-danger" : "text-fg-2",
              )}
            >
              {previewPath}
            </p>
            {previewInvalid && (
              <p className="text-12-medium text-danger">{t("feature.categories.errors.invalidCategoryPath")}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="jaLabel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.category.label.ja")}</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="enLabel"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.category.label.en")}</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ""} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.description")}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={3} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {typeId === "relation" && (
          <FormField
            control={form.control}
            name="relationTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.relationType.modelName")}</FormLabel>
                <Select value={field.value ?? ""} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("model.relationType.modelName")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {relationTypes.map((rt) => (
                      <SelectItem key={rt.id} value={rt.id}>
                        {rt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="documentUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.document")}</FormLabel>
              <FormControl>
                <Input {...field} value={field.value ?? ""} placeholder="https://…" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
